import {AppDispatch} from '../store';
import {AccountType} from '../types/AccountType';
import {UserPermission} from '../types/enums/UserPermission';
import {ExternalUserRole, InternalUserRole, UserRole} from '../types/enums/UserRole';
import {getJsonData, jsonDataWithHeader, postJsonData} from './requests';

export type UserSearchParams = {
    roleFilter?: 'INTERNAL' | 'EXTERNAL'
    activeFilter?: 'ONLY_ACTIVE' | 'ONLY_INACTIVE'
    searchQuery?: string
    page?: number
    matchNamesOnly?: boolean
    permission?: UserPermission
}

const createSearchParams = (searchParams: UserSearchParams) => {
    const searchParam = new URLSearchParams();

    if (searchParams.roleFilter) {
        searchParam.append('roleFilter', searchParams.roleFilter);
    }

    if (searchParams.activeFilter) {
        searchParam.append('activeFilter', searchParams.activeFilter);
    }

    if (searchParams.searchQuery) {
        searchParam.append('searchQuery', searchParams.searchQuery);
    }

    if (!!searchParams.page || searchParams.page === 0) {
        searchParam.append('page', '' + searchParams.page);
    }

    if (searchParams.permission) {
        searchParam.append('permission', searchParams.permission);
    }

    if (searchParams.matchNamesOnly) {
        searchParam.append('matchNamesOnly', String(searchParams.matchNamesOnly));
    }

    return searchParam;
};

export const findMe = (dispatch: AppDispatch, header: Headers) => {
    return jsonDataWithHeader('api/1.0/accounts/me', 'GET', false, dispatch, header);
};

export const searchAccounts = (dispatch: AppDispatch, searchParams: UserSearchParams) => {
    const searchParam = createSearchParams(searchParams);
    const url = 'api/1.0/accounts/search?' + searchParam.toString();
    return getJsonData(url, dispatch);
};

export const countAccounts = (dispatch: AppDispatch, searchParams: UserSearchParams) => {
    const searchParam = createSearchParams(searchParams);
    const url = 'api/1.0/accounts/count?' + searchParam.toString();
    return getJsonData(url, dispatch);
};

export const countAccountsPerFilter = (dispatch: AppDispatch) => {
    const url = 'api/1.0/accounts/count-per-filter';
    return getJsonData(url, dispatch);
};

export const searchAdAccount = (dispatch: AppDispatch, query: string) => {
    const url = 'api/1.0/accounts/internal/search/' + encodeURIComponent(query);
    return getJsonData(url, dispatch);
};

export const createOrUpdateAccount = (dispatch: AppDispatch, account: AccountType) => {
    const url = 'api/1.0/accounts';
    return postJsonData(url, JSON.stringify(account), dispatch);
};

export const acceptDataPrivacy = (dispatch: AppDispatch) => {
    const url = 'api/1.0/accounts/accept-data-privacy';
    return postJsonData(url, undefined, dispatch);
};

export const acceptTermsOfUse = (dispatch: AppDispatch) => {
    const url = 'api/1.0/accounts/accept-terms-of-use';
    return postJsonData(url, undefined, dispatch);
};

export const searchExternalAccount = (dispatch: AppDispatch, email: string) => {
    const url = `api/1.0/accounts/external/exists/` + encodeURIComponent(email);
    return getJsonData(url, dispatch);
};

export const resendInvitationMail = (dispatch: AppDispatch, email: string) => {
    const url = `api/1.0/accounts/external/resend-invitation-mail/` + encodeURIComponent(email);
    return postJsonData(url, undefined, dispatch);
};

export const findAccountNamesByIds = (dispatch: AppDispatch, ids: (number | string)[]) => {
    const searchParams = new URLSearchParams();
    for (const id of ids) {
        searchParams.append('id', '' + id);
    }

    const url = `api/1.0/accounts/names?` + searchParams.toString();

    return getJsonData(url, dispatch);
};

export const findAccountById = (dispatch: AppDispatch, id: number) => {
    const url = `api/1.0/accounts/${id}`;
    return getJsonData(url, dispatch);
};

export const findEditorNameForAccountId = (dispatch: AppDispatch, id: number) => {
    const url = `api/1.0/accounts/${id}/editor-name`;
    return getJsonData(url, dispatch);
};

export const getDefaultPermissionsForRole = (dispatch: AppDispatch, role: UserRole | ExternalUserRole | InternalUserRole) => {
    const url = `api/1.0/accounts/default-roles/${role}`;
    return getJsonData(url, dispatch);
};
